import { useCallback, useMemo, useState } from 'react';
import { useDebounce } from '@ais-ui/custom-hooks';
import { Job, ReportTemplate } from '@ais-ui/websockets';

export type SortDirection = 'asc' | 'desc';
export type ReplayVideoSortType = 'name' | 'date' | 'status';

export interface ReplayVideoFilters {
  status: 'all' | 'finished' | 'failed' | 'executing' | 'pending' | 'aborted';
}

export interface SortConfig {
  type: ReplayVideoSortType;
  direction: SortDirection;
}

export interface FlattenedReplayVideo {
  report: Job;
  reportTemplate: ReportTemplate;
}

export const useReplayVideoFilters = (
  replayVideoTemplates: ReportTemplate[],
  search: string
) => {
  const debouncedSearch = useDebounce(
    search.length >= 3 ? search : '',
    200,
    ''
  );

  const [filters, setFilters] = useState<ReplayVideoFilters>({
    status: 'all',
  });

  const [sortConfig, setSortConfig] = useState<SortConfig>({
    type: 'date',
    direction: 'desc',
  });

  // Flatten templates into individual replay videos
  const flattenedVideos = useMemo((): FlattenedReplayVideo[] => {
    const videos: FlattenedReplayVideo[] = [];
    replayVideoTemplates.forEach((template) => {
      template.jobs.forEach((job) => {
        if (job.status !== 'NONE') {
          videos.push({ report: job, reportTemplate: template });
        }
      });
    });
    return videos;
  }, [replayVideoTemplates]);

  const filterByStatus = useCallback(
    (
      videos: FlattenedReplayVideo[],
      statusFilter: ReplayVideoFilters['status']
    ): FlattenedReplayVideo[] => {
      if (statusFilter === 'all') {
        return videos;
      }

      return videos.filter((video) => {
        switch (statusFilter) {
          case 'finished':
            return (
              video.report.status === 'FINISHED' ||
              video.report.status === 'FINISHED_SCHEDULED'
            );
          case 'failed':
            return video.report.status === 'FAILED';
          case 'executing':
            return video.report.status === 'EXECUTING';
          case 'pending':
            return video.report.status === 'PENDING';
          case 'aborted':
            return video.report.status === 'ABORTED';
          default:
            return true;
        }
      });
    },
    []
  );

  const filterBySearch = useCallback(
    (
      videos: FlattenedReplayVideo[],
      searchTerm: string
    ): FlattenedReplayVideo[] => {
      if (!searchTerm) return videos;

      const lowerSearch = searchTerm.toLowerCase();
      return videos.filter(
        (video) =>
          video.report.name.toLowerCase().includes(lowerSearch) ||
          video.reportTemplate.tplName.toLowerCase().includes(lowerSearch) ||
          video.report.uuid.toLowerCase().includes(lowerSearch)
      );
    },
    []
  );

  const sortVideos = useCallback(
    (
      videos: FlattenedReplayVideo[],
      config: SortConfig
    ): FlattenedReplayVideo[] => {
      const sorted = [...videos];
      const { type: sortBy, direction } = config;

      switch (sortBy) {
        case 'name':
          sorted.sort((a, b) => {
            const nameA = `${a.reportTemplate.tplName} - ${a.report.name}`;
            const nameB = `${b.reportTemplate.tplName} - ${b.report.name}`;
            return nameA.localeCompare(nameB);
          });
          break;
        case 'date':
          sorted.sort(
            (a, b) =>
              new Date(b.report.creationdate).getTime() -
              new Date(a.report.creationdate).getTime()
          );
          break;
        case 'status':
          sorted.sort((a, b) => {
            const statusPriority: Record<string, number> = {
              EXECUTING: 1,
              PENDING: 2,
              FAILED: 3,
              FINISHED: 4,
              FINISHED_SCHEDULED: 5,
              ABORTED: 6,
              NONE: 7,
            };

            const priorityA = statusPriority[a.report.status] || 8;
            const priorityB = statusPriority[b.report.status] || 8;

            return priorityA - priorityB;
          });
          break;
      }

      // Apply direction
      if (direction === 'desc' && sortBy !== 'date') {
        sorted.reverse();
      } else if (direction === 'asc' && sortBy === 'date') {
        sorted.reverse();
      }

      return sorted;
    },
    []
  );

  const filteredAndSortedVideos = useMemo(() => {
    let filtered = flattenedVideos;

    filtered = filterBySearch(filtered, debouncedSearch);
    filtered = filterByStatus(filtered, filters.status);

    return sortVideos(filtered, sortConfig);
  }, [
    debouncedSearch,
    filterBySearch,
    filterByStatus,
    filters.status,
    flattenedVideos,
    sortVideos,
    sortConfig,
  ]);

  return {
    filteredAndSortedVideos,
    filters,
    setFilters,
    sortConfig,
    setSortConfig,
  };
};
